import { Controller } from "@hotwired/stimulus"

// Owns a multi-file image <input>: on `change` it validates count/type/size,
// trims the selection to what's allowed, and renders thumbnail previews with
// remove buttons. Clears itself on the form's `reset`. Pairs with `file-dropzone`,
// which feeds dropped files in by firing `change`.
//
//   <form data-controller="image-uploads" data-action="reset->image-uploads#clear"
//         data-image-uploads-max-files-value="3"
//         data-image-uploads-max-bytes-value="5242880"
//         data-image-uploads-accept-value="image/png,image/jpeg,image/gif">
//     <input type="file" multiple data-image-uploads-target="input"
//            data-action="change->image-uploads#changed">
//     <div data-image-uploads-target="previews"></div>
//     <div data-image-uploads-target="error"></div>
//   </form>
export default class extends Controller {
  static targets = ["input", "previews", "error"]

  static values = {
    maxFiles: { type: Number, default: 3 },
    maxBytes: { type: Number, default: 5 * 1024 * 1024 },
    accept: { type: String, default: "image/png,image/jpeg,image/gif" },
  }

  changed() {
    const accepted = this.acceptValue.split(",")
    const maxMb = Math.round(this.maxBytesValue / 1024 / 1024)
    const errors = []
    const transfer = new DataTransfer()

    Array.from(this.inputTarget.files).forEach((file) => {
      if (transfer.items.length >= this.maxFilesValue) {
        errors.push(`Up to ${this.maxFilesValue} images.`)
      } else if (!accepted.includes(file.type)) {
        errors.push(`${file.name} isn't a PNG, JPG, or GIF.`)
      } else if (file.size > this.maxBytesValue) {
        errors.push(`${file.name} is larger than ${maxMb} MB.`)
      } else {
        transfer.items.add(file)
      }
    })

    this.inputTarget.files = transfer.files
    if (errors.length) this.showError([...new Set(errors)].join(" "))
    else this.clearError()
    this.renderPreviews()
  }

  removeFile(index) {
    const transfer = new DataTransfer()
    Array.from(this.inputTarget.files).forEach((file, i) => {
      if (i !== index) transfer.items.add(file)
    })
    this.inputTarget.files = transfer.files
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Wired to the form `reset` (which has already emptied the input).
  clear() {
    this.clearError()
    this.renderPreviews()
  }

  renderPreviews() {
    if (!this.hasPreviewsTarget) return
    this.previewsTarget.innerHTML = ""

    Array.from(this.inputTarget.files).forEach((file, index) => {
      const url = URL.createObjectURL(file)

      const wrapper = document.createElement("div")
      wrapper.className =
        "relative aspect-square overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-700"

      const img = document.createElement("img")
      img.src = url
      img.className = "h-full w-full object-cover"
      img.onload = () => URL.revokeObjectURL(url)
      img.onerror = () => URL.revokeObjectURL(url)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.setAttribute("aria-label", "Remove image")
      remove.className =
        "absolute right-1 top-1 inline-flex size-5 items-center justify-center rounded-full bg-black/60 text-xs text-white"
      remove.innerHTML = "&times;"
      remove.addEventListener("click", () => this.removeFile(index))

      wrapper.append(img, remove)
      this.previewsTarget.append(wrapper)
    })
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
